/**
 * Dropdown effect on the main menu on desktop.
 *
 * @package Qustodio\Theme
 */

import styles from '../../../helpers/styles';
import dom from '../../../helpers/dom';
import headerElements from './headerElements';

const menuDesktopDropdown = () => {
  const dropdownClass = 'qusd-menu-main-desktop__dropdown';
  const arrowIconClass = 'fa-chevron-down';
  const flipArrowIconClass = 'fa-flip-vertical';

  let dropdownElements;
  let activeDropdownElement;

  /**
   * If there's an active dropdown, which type of event was used to display it.
   *
   * Depending on the event type, the behavior to hide the dropdown menu may
   * change slightly. For example, if the dropdown menu was displayed by
   * clicking on the trigger, we may not want to hide it the mouse is moved out
   * of the dropdown menu.
   *
   * @type {string} 'click', 'focus', 'hover' or undefined.
   */
  let displayMethod;

  const isOpen = (dropdownElement) => {
    const activeDropdownSet = typeof activeDropdownElement !== 'undefined';
    const isActiveDropdown = dropdownElement === activeDropdownElement;
    return activeDropdownSet && isActiveDropdown;
  };

  const hide = (dropdownElement, triggerElement) => {
    if (isOpen(dropdownElement)) {
      const arrowIcon = triggerElement.querySelector(dom.classNameToSelector(arrowIconClass));
      arrowIcon.classList.remove(flipArrowIconClass);
      displayMethod = undefined;
      activeDropdownElement = undefined;
      styles.hideElement(dropdownElement, true);
    }
  };

  const show = (dropdownElement, triggerElement) => {
    if (!isOpen(dropdownElement)) {
      const arrowIcon = triggerElement.querySelector(dom.classNameToSelector(arrowIconClass));
      arrowIcon.classList.add(flipArrowIconClass);
      hide(activeDropdownElement);
      activeDropdownElement = dropdownElement;
      styles.showElement(dropdownElement);
    }
  };

  const onTriggerClick = (dropdownElement, triggerElement, event) => {
    event.preventDefault();
    const didLastClickOpenDropdown = displayMethod === 'click' && isOpen(dropdownElement);
    if (didLastClickOpenDropdown) {
      hide(dropdownElement, triggerElement);
    } else {
      displayMethod = 'click';
      show(dropdownElement, triggerElement);
    }
  };

  const onTriggerFocus = (dropdownElement, triggerElement) => {
    const displayMethodTakesPrecedence = displayMethod === undefined || displayMethod === 'hover';
    if (displayMethodTakesPrecedence) {
      displayMethod = 'focus';
      show(dropdownElement, triggerElement);
    }
  };

  const onContainerHovered = (dropdownElement, triggerElement) => {
    const displayMethodTakesPrecedence = displayMethod === undefined;
    if (displayMethodTakesPrecedence) {
      displayMethod = 'hover';
      show(dropdownElement, triggerElement);
    }
  };

  const onContainerOutOfFocus = (dropdownElement, triggerElement, event) => {
    const displayMethodIsAffected = displayMethod === 'focus' || displayMethod === 'click';
    if (displayMethodIsAffected) {
      const newFocusedItem = event.relatedTarget;
      if (!dropdownElement.parentElement.contains(newFocusedItem)) {
        hide(dropdownElement, triggerElement);
      }
    }
  };

  const onContainerUnhovered = (dropdownElement, triggerElement, event) => {
    const displayMethodIsAffected = displayMethod === 'hover';
    if (displayMethodIsAffected) {
      const newHoveredItem = event.relatedTarget;
      if (!dropdownElement.parentElement.contains(newHoveredItem)) {
        hide(dropdownElement, triggerElement);
      }
    }
  };

  const init = () => {
    dropdownElements = Array.from(
      headerElements.getDesktopHeader().querySelectorAll(dom.classNameToSelector(dropdownClass)),
    );

    if (dropdownElements !== null) {
      dropdownElements.forEach((dropdownElement) => {
        const containerElement = dropdownElement.parentElement;
        const triggerElement = dropdownElement.previousElementSibling;

        // Events that can either display or hide the dropdown.
        triggerElement.addEventListener('click', (event) => onTriggerClick(dropdownElement, triggerElement, event));

        // Events that can display the dropdown.
        triggerElement.addEventListener('focus', () => onTriggerFocus(dropdownElement, triggerElement));
        containerElement.addEventListener('focusin', () => onTriggerFocus(dropdownElement, triggerElement));
        containerElement.addEventListener('mouseover', () => onContainerHovered(dropdownElement, triggerElement));

        // Events that can hide the dropdown.
        containerElement.addEventListener('focusout', (event) => onContainerOutOfFocus(dropdownElement, triggerElement, event));
        containerElement.addEventListener('mouseout', (event) => onContainerUnhovered(dropdownElement, triggerElement, event));
      });
    }
  };

  return {
    init,
  };
};

export default menuDesktopDropdown();
